<template>
    <div>
        <!-- Board: relics + stardust left, 3x3 main grid, halo + echostones right -->
        <div class="eq-board">
            <div class="eq-col">
                <equip-slot v-for="s in leftSlots" :key="s.pocket" :label="s.label" :entry="s.entry" />
            </div>
            <div class="eq-main">
                <equip-slot v-for="s in mainSlots" :key="s.area" :style="{ gridArea: s.area }"
                    :label="s.label" :entry="s.entry" :tall="s.tall" :set="s.set"
                    @select-set="(v: WeaponSet) => selectSet(s.area, v)" />
            </div>
            <div class="eq-col">
                <equip-slot v-for="s in rightSlots" :key="s.pocket" :label="s.label" :entry="s.entry" />
            </div>
        </div>

        <!-- Everything else worn (style slots, face, hair...) -->
        <div v-if="otherEntries.length" class="mt-3">
            <div class="text-caption text-grey mb-1">Other slots</div>
            <div class="d-flex flex-wrap" style="gap: 6px">
                <v-tooltip v-for="o in otherEntries" :key="o.pocket" location="bottom" content-class="item-tip-content"
                    :disabled="!o.entry.tip" :open-delay="150">
                    <template #activator="{ props }">
                        <v-chip v-bind="props" size="small" variant="tonal">
                            <span class="text-grey mr-1">{{ o.label }}</span>{{ o.entry.name }}
                        </v-chip>
                    </template>
                    <item-tip v-if="o.entry.tip" :title="o.entry.name" :tip="o.entry.tip" />
                </v-tooltip>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, computed, reactive, watch, type PropType } from 'vue';
import type { IndexItem, Holder, EquipmentItem } from '@/items/itemIndex';
import { buildTip, enchantText, pocketName } from '@/items/itemTooltip';
import { itemName, ensureItemNames } from '@/items/itemNames';
import EquipSlot, { type SlotEntry, type WeaponSet } from './equipSlot.vue';
import ItemTip from './itemTip.vue';

const SLOT_LABELS: Record<number, string> = {
    8: 'Head', 5: 'Clothing', 6: 'Gloves', 7: 'Shoes', 9: 'Robe',
    16: 'Accessory L', 17: 'Accessory R',
    10: 'Main Hand', 13: 'Off Hand', 11: 'Main Hand', 14: 'Off Hand',
    32: 'Relic 1', 33: 'Relic 2', 34: 'Relic 3', 35: 'Relic 4',
    51: 'Halo', 54: 'Stardust', 62: 'Echostone 1', 63: 'Echostone 2', 64: 'Echostone 3',
};
const WEAPON_POCKETS = new Set([10, 11, 13, 14]);
const ARMOR_POCKETS = new Set([5, 6, 7, 8, 9]);

// The two weapon areas resolve their pocket through the I/II toggle (I = 10/13, II = 11/14).
type WeaponArea = 'wl' | 'wr';
const WEAPON_AREA_POCKETS: Record<WeaponArea, Record<WeaponSet, number>> = {
    wl: { I: 10, II: 11 }, wr: { I: 13, II: 14 },
};
const MAIN_AREAS: { area: string; pocket?: number; tall?: boolean }[] = [
    { area: 'head', pocket: 8 }, { area: 'accl', pocket: 16 }, { area: 'accr', pocket: 17 },
    { area: 'wl', tall: true }, { area: 'body', pocket: 5, tall: true }, { area: 'wr', tall: true },
    { area: 'hand', pocket: 6 }, { area: 'foot', pocket: 7 }, { area: 'robe', pocket: 9 },
];
const LEFT_POCKETS = [32, 33, 34, 35, 54];
const RIGHT_POCKETS = [51, 62, 63, 64];
const BOARD_POCKETS = new Set([...Object.keys(SLOT_LABELS).map(Number)]);

// One character's worn gear as an in-game-style board. Items without detail
// (other players: item id and dyes only) still show name and dyes.
export default defineComponent({
    components: { EquipSlot, ItemTip },
    props: {
        items: { type: Array as PropType<EquipmentItem[]>, required: true },
    },
    setup(props) {
        watch(() => props.items, items => { void ensureItemNames(items.map(e => e.item.id)); }, { immediate: true });

        const brief = (it: IndexItem, pocket: number): string => {
            if (WEAPON_POCKETS.has(pocket) && it.attackMax) {
                return `Atk ${it.attackMin ?? 0}~${it.attackMax} Bal ${it.balance ?? 0} Crit ${it.critical ?? 0}`;
            }
            if (ARMOR_POCKETS.has(pocket) && (it.defense || it.protection)) {
                return `Def ${it.defense ?? 0} Prot ${it.protection ?? 0}`;
            }
            return enchantText(it);
        };

        const entryByPocket = computed(() => {
            const m = new Map<number, SlotEntry>();
            for (const e of props.items) {
                const h: Holder = { ...e.item, entity: '', master: '' };
                m.set(e.pocket, { item: e.item, name: itemName(e.item.id), brief: brief(e.item, e.pocket), tip: buildTip(h) });
            }
            return m;
        });

        const cell = (pocket: number) => ({
            pocket, label: SLOT_LABELS[pocket] ?? `#${pocket}`, entry: entryByPocket.value.get(pocket),
        });

        const weaponSet = reactive<Record<WeaponArea, WeaponSet>>({ wl: 'I', wr: 'I' });
        const isWeaponArea = (area: string): area is WeaponArea => area === 'wl' || area === 'wr';
        const selectSet = (area: string, v: WeaponSet) => {
            if (isWeaponArea(area)) weaponSet[area] = v;
        };

        const leftSlots = computed(() => LEFT_POCKETS.map(cell));
        const rightSlots = computed(() => RIGHT_POCKETS.map(cell));
        const mainSlots = computed(() => MAIN_AREAS.map(a => {
            const set = isWeaponArea(a.area) ? weaponSet[a.area] : undefined;
            const pocket = a.pocket ?? WEAPON_AREA_POCKETS[a.area as WeaponArea][set ?? 'I'];
            return { area: a.area, tall: !!a.tall, set, ...cell(pocket) };
        }));

        const otherEntries = computed(() => [...entryByPocket.value.entries()]
            .filter(([pocket]) => !BOARD_POCKETS.has(pocket))
            .sort((a, b) => a[0] - b[0])
            .map(([pocket, entry]) => ({
                pocket, entry,
                label: (pocketName(pocket) ?? `Pocket ${pocket}`).replace(/^(Equip|Style) - /, '$1: '),
            })));

        return { leftSlots, rightSlots, mainSlots, selectSet, otherEntries };
    },
});
</script>

<style scoped>
.eq-board { display: flex; gap: 12px; align-items: flex-start; flex-wrap: wrap; }
.eq-col { display: flex; flex-direction: column; gap: 6px; }
.eq-main {
    display: grid;
    grid-template-columns: repeat(3, 128px);
    grid-template-rows: 96px 160px 96px;
    grid-template-areas: "head accl accr" "wl body wr" "hand foot robe";
    gap: 6px;
}
.eq-col :deep(.eq-slot) { min-height: 64px; }
</style>
